import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ShareNetwork } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ShareListDialog({ open, onClose, list, users, onShared }) {
  const { t } = useI18n();
  const [selectedUsers, setSelectedUsers] = useState([]);

  const toggleUser = (userId) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const handleShare = async () => {
    if (!list || selectedUsers.length === 0) return;
    try {
      await api.post(`/lists/${list.id}/share`, { user_ids: selectedUsers });
      setSelectedUsers([]);
      onShared?.();
      onClose();
      toast.success(t('settings.shareList') + ' ✓');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleClose = () => {
    setSelectedUsers([]);
    onClose();
  };

  const availableUsers = list ? users.filter(u => !list.shared_with?.includes(u.id) && u.id !== list.owner_id) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="share-list-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShareNetwork size={18} /> {t('settings.shareList')}
          </DialogTitle>
        </DialogHeader>
        {list && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: list.color }} />
              <span className="text-sm font-medium">{list.name}</span>
              {list.shared_with?.length > 0 && (
                <Badge variant="secondary" className="text-[10px]">{t('settings.sharedWith')} {list.shared_with.length}</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t('settings.selectUsers')}</p>
            <div className="space-y-2">
              {availableUsers.map(u => (
                <label key={u.id} className="flex items-center gap-3 p-2 rounded-lg border border-border hover:bg-muted/50 cursor-pointer" data-testid={`share-select-user-${u.id}`}>
                  <Checkbox checked={selectedUsers.includes(u.id)} onCheckedChange={() => toggleUser(u.id)} />
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: u.avatar_color }}>{u.name?.[0]?.toUpperCase()}</div>
                  <span className="text-sm">{u.name}</span>
                </label>
              ))}
              {availableUsers.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">{t('settings.noUsers')}</p>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>{t('common.cancel')}</Button>
          <Button data-testid="confirm-share-btn" onClick={handleShare} disabled={selectedUsers.length === 0}>{t('settings.share')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
